import React, { useEffect, useState } from "react";
import PageLayout from "../../layouts/PageLayout";
import { useLocation, useNavigate } from "react-router-dom";
import { TCustomer } from "../../types/Types";
import apiRequest from "../../helpers/apiRequest";
import { Urls } from "../../utils/urls";
import Loading from "../../components/loading/Loading";
import { routes } from "../../utils/routes";
import ProfileHeader from "./components/customerDetails/ProfileHeader";
import InfoCard from "./components/customerDetails/InfoCard";
import AdminControls from "./components/customerDetails/AdminControls";
import SubjectsList from "./components/customerDetails/SubjectsList";

const CustomerDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<TCustomer>();
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const { userId } = location.state ?? {};
  const fetchCustomer = async () => {
    const { data, error } = await apiRequest<{ userData: TCustomer }>({
      method: "GET",
      url: `${Urls.get_one_user}/${userId}`,
    });
    if (data) {
      setCustomer(data.userData);
    } else if (error) {
      alert(error);
    }
  };

  const handleClearLoginData = async () => {
    const confirm = window.confirm("Clear all login sessions for this user?");
    if (!confirm) return;

    const { data, error } = await apiRequest<{ userData: TCustomer }>({
      method: "GET",
      url: `${Urls.clear_login_data}/${customer?._id}`,
    });
    if (data) {
      alert("Device data cleared successfully.");
    } else if (error) {
      alert(error);
    }
  };

  const handleEdit = () => {
    navigate(routes.update_customer_data, {
      state: {
        customer,
      },
    });
  };

  const handleDisable = async () => {
    const action = customer?.disabled ? "enable" : "disable";
    const confirm = window.confirm(
      `Are you sure you want to ${action} this user?`,
    );
    if (!confirm) return;

    const { data, error } = await apiRequest<{ user: TCustomer }>({
      method: "POST",
      url: `${Urls.update_user}/${customer?._id}`,
      data: { disabled: !customer?.disabled },
    });
    if (data) {
      fetchCustomer();
    } else if (error) {
      alert(error);
    }
  };

  const handleToggleApproval = async (
    subjectId: string,
    currentStatus: boolean,
  ) => {
    if (!customer) return;
    setIsUpdating(subjectId);

    const updatedSubjects = customer.subjects
      .filter((s) => s.subjectId)
      .map((s) => ({
        subjectId: s.subjectId._id,
        approved: s.subjectId._id === subjectId ? !currentStatus : s.approved,
      }));

    const { data, error } = await apiRequest<{ user: TCustomer }>({
      method: "POST",
      url: `${Urls.update_user}/${customer._id}`,
      data: { subjects: updatedSubjects },
    });

    if (data) {
      fetchCustomer();
    } else if (error) {
      alert(error);
    }
    setIsUpdating(null);
  };

  useEffect(() => {
    if (!userId) {
      navigate(-1);
      return;
    }
    fetchCustomer();
  }, [userId]);

  if (!customer) return <Loading />;

  return (
    <PageLayout label="User Profile">
      <div className="max-w-6xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <ProfileHeader customer={customer} onEdit={handleEdit} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-6">
            <InfoCard customer={customer} />
            <AdminControls
              customer={customer}
              onClearSessions={handleClearLoginData}
              onToggleDisable={handleDisable}
            />
          </div>

          <div className="lg:col-span-2 space-y-6">
            <SubjectsList
              subjects={customer.subjects}
              isUpdating={isUpdating}
              onToggleApproval={handleToggleApproval}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default CustomerDetails;
